import { useEffect, useRef, useState } from 'react'
import { ownerApi } from 'lib/owner-notes/api'
import { useOwnerCredentialOptional, useOwnerSessionOptional } from 'lib/owner-notes/auth-bridge'
import { hasDeviceMarker, useDeviceEpoch } from 'lib/owner-notes/declared'
import { helpfulBatches, helpfulVotes } from 'lib/community/helpful'
import type { CommunityHelpfulAnswer } from 'lib/community/types'

/** Wait this long after the ids change before asking: the first render and a page of rows
 *  appended right after it make one read, not two. */
const DEBOUNCE_MS = 250

const NONE: ReadonlyMap<string, CommunityHelpfulAnswer> = new Map()

/**
 * The reader's own votes on the reviews a list shows (community feed rows): one batched
 * `GET …/helpful/me?note_ids=` per id-set change (chunked at `HELPFUL_BATCH_MAX`), through
 * `ownerApi.helpfulMineBatch`. Returns DealHunt's `{ helpful, mine, value, vote_counts }` by note
 * id (`value` / `vote_counts` only when DealHunt sends them), for each row's control to apply with
 * `helpfulRead`.
 *
 * Who is asking: a signed-in reader (an owner credential with a token — the session's bearer), or,
 * signed out, a browser with an owner-notes device (the marker cookie; contract #202 — a
 * signed-out reader votes with the device, so its votes must survive a refresh). Neither: it never
 * fires. A failed or non-answer chunk (DealHunt before the batch route, a proxy page) adds nothing:
 * those rows stay UNKNOWN — a press on them changes no count until DealHunt answers. The map is
 * keyed to who asked (the session token, else the device epoch), so a sign-in, sign-out, account
 * switch or a freshly minted device never shows another voter's votes. Effects only — nothing
 * reaches the server HTML.
 */
export function useHelpfulMine(
  noteIds: readonly string[]
): ReadonlyMap<string, CommunityHelpfulAnswer> {
  const credential = useOwnerCredentialOptional()
  // Signed out is known only once the session check answered: a device read before it would be
  // a wasted call for a signed-in reader, whose session arrives a moment later.
  const { resolved } = useOwnerSessionOptional()
  // The credential object is rebuilt on every render; the token is what identifies the session.
  const token = credential?.token ?? null
  const credentialRef = useRef(credential)
  credentialRef.current = credential
  // A minted (or forgotten) device bumps the epoch: a signed-out reader's read starts again.
  const epoch = useDeviceEpoch()
  const who = token ?? (resolved ? `device:${epoch}` : null)
  const [votes, setVotes] = useState<{
    who: string | null
    map: ReadonlyMap<string, CommunityHelpfulAnswer>
  }>({ who: null, map: NONE })
  // A stable key for the same set of ids, whatever the array's identity or order.
  const key = helpfulBatches(noteIds)
    .map(chunk => chunk.join(','))
    .join(';')

  useEffect(() => {
    const cred = token ? credentialRef.current : null
    if (!key || !who || (token && !cred) || (!token && !hasDeviceMarker())) {
      return undefined
    }
    let live = true
    const timer = setTimeout(() => {
      const chunks = key.split(';').map(chunk => chunk.split(','))
      void Promise.allSettled(chunks.map(ids => ownerApi.helpfulMineBatch(cred, ids))).then(
        results => {
          if (!live) {
            return
          }
          const map = new Map<string, CommunityHelpfulAnswer>()
          for (const result of results) {
            if (result.status === 'fulfilled') {
              helpfulVotes(result.value).forEach((answer, id) => map.set(id, answer))
            }
          }
          setVotes({ who, map })
        }
      )
    }, DEBOUNCE_MS)
    return () => {
      live = false
      clearTimeout(timer)
    }
  }, [key, token, who])

  return who && votes.who === who ? votes.map : NONE
}
